// AdminRoute.tsx - Route wrapper that requires admin privileges
import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, ShieldAlert } from 'lucide-react';

interface AdminRouteProps {
  children: ReactNode;
  permission?: string;
  superAdminOnly?: boolean;
}

function AdminRoute({ children, permission, superAdminOnly = false }: AdminRouteProps): React.ReactElement {
  const { user, loading } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-root flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-indigo mx-auto mb-4" />
          <div className="text-xl text-body">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has admin privileges
  const lacksPlatformRole = !user.platform_role || user.platform_role.priority <= 0;
  const lacksPermission = permission && user.platform_role?.slug !== 'super_admin' && !user.permissions?.includes(permission);
  const lacksSuperAdmin = superAdminOnly && user.platform_role?.slug !== 'super_admin';
  if (lacksPlatformRole || lacksPermission || lacksSuperAdmin) {
    return (
      <div className="min-h-screen bg-root flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-inner rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-primary mb-2">Access Denied</h1>
          <p className="text-body mb-6">
            {lacksPlatformRole
              ? "You don't have a platform staff role for this page."
              : lacksSuperAdmin
                ? 'This page requires the super admin role.'
                : `Your platform role does not include ${permission}.`}
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-gradient-primary text-white px-6 py-2.5 rounded-lg hover-shadow-primary transition font-semibold"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and has a platform role - render children
  return <>{children}</>;
}

export default AdminRoute;
